/**
 * CardEntryForm — the shared new-payment-method entry block.
 *
 * Renders the Stripe **Payment Element** or the Square hosted form for the detected
 * provider, along with the loading / SetupIntent-error / no-provider states. The
 * Payment Element renders whatever the (server-resolved) SetupIntent enables — card
 * fields, or Stripe's native card/bank accordion when the club offers ACH — so there
 * is no app-drawn toggle. Extracted from the booking, subscription, and account-setup
 * pages, which all rendered this identical block.
 *
 * Presentational: the SetupIntent wiring and persist flow live in {@link useCardSave}
 * — this component renders what it is handed and calls `onCardSuccess` with the
 * client-confirmed setup-intent id (Stripe) or the nonce (Square). Stripe confirm
 * failures are surfaced inline by `StripePaymentElementForm`; here we only report
 * them to monitoring via the shared `reportClientPaymentError` (infra alerts, declines quiet).
 *
 * @module features/payment/components/CardEntryForm
 */

import { useMemo } from 'react';
import { StripePaymentElementForm, buildStripeAppearance } from '@butter/ui/domains/billing';
import type { ConfirmSetupResult } from '@butter/ui/hooks/payment';
import { reportClientPaymentError } from '@butter/monitoring';
import { describeStripeConfirmError, realEmail } from '@butter/library/shared';
import { SquarePaymentForm } from '~/features/payment/providers/square/SquarePaymentForm';
import type { StripeSetupIntent } from '~/features/payment/hooks/useStripeSetupIntent';

type SquarePaymentSettings =
  | React.ComponentProps<typeof SquarePaymentForm>['paymentSettings']
  | null
  | undefined;

export interface CardEntryFormProps {
  /** Detected provider, or `null` when none is configured (renders a notice). */
  paymentProvider: 'stripe' | 'square' | null;
  /** The (multi-method) SetupIntent (clientSecret + publishableKey), null until fetched. */
  stripeSetupIntent: StripeSetupIntent | null;
  stripeSetupIntentLoading: boolean;
  stripeSetupIntentError: string | null;
  retryStripeSetupIntent: () => void;
  /** Club payment settings for the Square hosted form. */
  paymentSettings: SquarePaymentSettings;
  /** Called with the provider token/nonce once the instrument is entered + confirmed. */
  /**
   * Receives the Stripe `seti_…` or the Square nonce. Both providers AWAIT this, so
   * the persist runs inside the in-flight window `onSubmittingChange` reports —
   * the return type must express that or an async handler is silently not awaited.
   */
  onCardSuccess: (providerToken: string) => void | Promise<void>;
  /** Prefill for the Payment Element billing email. */
  defaultEmail?: string;
  /** Extra context (userId / clubId) attached to confirm-error monitoring. */
  errorContext?: Record<string, unknown>;
  /** Prefix for the console.error log line (e.g. a page name). */
  errorLabel?: string;
  /**
   * Called with the selected method (`'card' | 'bank'`) as the user switches rows in
   * the Stripe accordion — lets the page's fee/total line react (card surcharge vs
   * ACH $0). Only fires for the Stripe Payment Element (Square is card-only).
   */
  onMethodChange?: (method: 'card' | 'bank') => void;
  /**
   * Called while the card save is in flight (Stripe confirm + the awaited persist).
   * Pages MUST use this to disable step/back controls: the SetupIntent is
   * single-use, so unmounting this form mid-flight spends it without persisting.
   */
  onSubmittingChange?: (submitting: boolean) => void;
  /** `id` for the provider form's `<form>`, so an external button can submit it. */
  formId?: string;
  /** Hide the provider form's internal submit button — pairs with `formId`. */
  hideSubmitButton?: boolean;
  /**
   * Fires `true` once a submit would go through — Square: card attached; Stripe:
   * element ready AND fields complete — and `false` on unmount.
   */
  onReadyChange?: (ready: boolean) => void;
}

export function CardEntryForm({
  paymentProvider,
  stripeSetupIntent,
  stripeSetupIntentLoading,
  stripeSetupIntentError,
  retryStripeSetupIntent,
  paymentSettings,
  onCardSuccess,
  defaultEmail,
  errorContext,
  errorLabel = 'CardEntryForm',
  onMethodChange,
  onSubmittingChange,
  formId,
  hideSubmitButton,
  onReadyChange,
}: CardEntryFormProps) {
  // Square SDK failures are captured inside SquarePaymentForm and rendered inline
  // there, so we only log — no redundant toast of the same message.
  const handleError = (err: string) => {
    console.error(`❌ [${errorLabel}] Payment error:`, err);
  };

  // Stripe confirm failures: the form renders the (branded) message inline; we
  // report to monitoring only. Infra alerts (`error`), declines stay `warning`.
  const handleConfirmError = (result: ConfirmSetupResult) => {
    reportClientPaymentError(result, {
      surface: 'web_add_payment_method',
      operation: 'confirm_setup',
      context: errorContext,
    });
  };

  // Stripe.js blocked (ad-blocker / corporate proxy) or the element failed to mount.
  // Rendered inline by the form; reported here because a whole tenant can be unable
  // to add a card while monitoring shows nothing.
  const handleLoadError = ({ error, code }: { error: string; code: string }) => {
    reportClientPaymentError(
      { error, code, isInfrastructureError: true },
      {
        surface: 'web_add_payment_method',
        operation: 'load_sdk',
        provider: 'stripe',
        context: errorContext,
      }
    );
  };

  // Theme-derived Stripe appearance, read once at mount (the element applies it
  // on mount; the tenant theme is stable per session).
  const appearance = useMemo(() => buildStripeAppearance(), []);

  return (
    <>
      {paymentProvider === 'stripe' && (
        <>
          {stripeSetupIntentLoading && (
            <p className="text-sm text-gray-500">Loading payment form…</p>
          )}
          {stripeSetupIntentError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{stripeSetupIntentError}</p>
              <button
                type="button"
                onClick={retryStripeSetupIntent}
                className="mt-2 text-sm font-medium text-red-800 underline text-interactive:text-red-900"
              >
                Try again
              </button>
            </div>
          )}
          {stripeSetupIntent && (
            <StripePaymentElementForm
              clientSecret={stripeSetupIntent.clientSecret}
              publishableKey={stripeSetupIntent.publishableKey}
              appearance={appearance}
              // A placeholder (imported member, no email on file) must not prefill Stripe —
              // it would show in the form and land in the card's billing_details.
              defaultEmail={realEmail(defaultEmail)}
              submitTestId="checkout-save-payment-method-btn"
              onMethodChange={onMethodChange}
              onSubmittingChange={onSubmittingChange}
              mapConfirmError={describeStripeConfirmError}
              onConfirmed={onCardSuccess}
              onConfirmError={handleConfirmError}
              onLoadError={handleLoadError}
              formId={formId}
              hideSubmitButton={hideSubmitButton}
              onReadyChange={onReadyChange}
            />
          )}
        </>
      )}

      {paymentProvider === 'square' &&
        (paymentSettings ? (
          <SquarePaymentForm
            paymentSettings={paymentSettings}
            onSuccess={onCardSuccess}
            onError={handleError}
            onSubmittingChange={onSubmittingChange}
            formId={formId}
            hideSubmitButton={hideSubmitButton}
            onReadyChange={onReadyChange}
          />
        ) : (
          // Square detected but its settings aren't available — never render blank.
          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-sm text-gray-600">
              Payment form is unavailable. Please refresh, or contact support if
              this continues.
            </p>
          </div>
        ))}

      {!paymentProvider && (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-sm text-gray-600">
            No payment provider configured. Please contact support.
          </p>
        </div>
      )}
    </>
  );
}
